import { v4 as uuidv4 } from 'uuid';

/**
 * Services seed data with complete information for all services.
 * Centralized location for service data to avoid duplication.
 */
export const servicesSeedData = [
  {
    id: uuidv4(),
    title: 'AI Advisory',
    shortDescription:
      'Board-ready AI strategy tied to value, risk, and execution.',
    description:
      'Move AI from experimentation to accountable capability: clear use cases, governance, investment sequencing, and measurable checkpoints from pilot to scale.',
    slug: 'ai-advisory',
    icon: 'ai',
    keyFeatures: [
      'AI Strategy & Investment Roadmaps',
      'Governance & Risk Operating Models',
      'Capability Maturity Assessment',
      'Operating Model Design',
      'Portfolio Prioritization',
      'Executive Enablement',
    ],
    benefits: [
      'Lower operating cost through targeted automation',
      'New revenue options from AI-enabled products',
      'Reduced implementation risk through governance',
      'Board-ready accountability and reporting',
      'Clear ROI path before scale',
    ],
    useCases: [
      'Enterprise AI command strategy',
      'Board-level governance frameworks',
      'Capability and maturity assessment',
      'Executive education and adoption planning',
      'Investment portfolio prioritization',
      'Regulatory and risk alignment',
    ],
    outcomes: [
      'Faster executive decisions with governed AI insight',
      'Improved customer outcomes through controlled personalization',
      'New revenue options from AI-enabled products',
      'Demonstrable compliance with governed AI processes',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Current state assessment, opportunity mapping, stakeholder alignment',
      strategy:
        '4-6 weeks: AI strategy development, governance framework, roadmap creation',
      implementation:
        '8-12 weeks: Pilot program launch, success metrics, scaling plan',
    },
  },
  {
    id: uuidv4(),
    title: 'Data Advisory',
    shortDescription:
      'Data strategy and architecture that make AI reliable.',
    description:
      'We design the data foundation AI depends on: governed sources, modern architecture, quality controls, and data products that improve decisions without weakening compliance.',
    slug: 'data-advisory',
    icon: 'data',
    keyFeatures: [
      'Enterprise Data Strategy',
      'Cloud-Native Data Architecture',
      'Governance, Privacy & Regulatory Compliance',
      'Data Quality & Observability',
      'Data Product Design',
      'AI-Ready Data Platform Engineering',
    ],
    benefits: [
      'Faster time-to-insight',
      'Lower compliance risk through governed data',
      'Reusable data products for growth teams',
      'Decision support from trusted pipelines',
      'AI-ready platform foundations',
    ],
    useCases: [
      'Enterprise data platform modernization',
      'Privacy and compliance automation',
      'Real-time decision support',
      'Data product development',
      'AI training data pipelines',
      'Cloud data architecture and migration',
    ],
    outcomes: [
      'Reduced data processing costs through cloud optimization',
      'Improved data quality with automated monitoring',
      'Accelerated analytics delivery with modern pipelines',
      'New revenue options enabled by data products',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Data maturity assessment, architecture review, compliance audit',
      design:
        '4-6 weeks: Data strategy, platform architecture, governance framework',
      implementation:
        '8-16 weeks: Platform build, data products, monitoring and optimization',
    },
  },
  {
    id: uuidv4(),
    title: 'Agentic AI Services',
    shortDescription:
      'Production agents with controls, evaluation, and measurable outcomes.',
    description:
      'We design agentic systems for real workflows: bounded authority, retrieval and tool use, human oversight, evaluation, and observability so automation can scale safely.',
    slug: 'agentic-ai-advisory',
    icon: 'agent',
    keyFeatures: [
      'Production Agent Architecture',
      'RAG & Tool-Use Implementation',
      'Safety Guardrails',
      'Evaluation & Monitoring',
      'MLOps / LLMOps Observability',
      'Human Oversight',
    ],
    benefits: [
      'Reduced manual workflow effort',
      'Consistent operation with clear escalation',
      'Measured accuracy through evaluation',
      'Pilot-to-production scale path',
      'Built-in safety and compliance monitoring',
    ],
    useCases: [
      'Autonomous customer support',
      'Document processing and intelligent workflow automation',
      'Real-time decision support for operations teams',
      'Compliance monitoring and automated reporting',
      'Supply chain optimization and predictive planning',
      'Financial analysis and risk assessment automation',
    ],
    outcomes: [
      'Lower customer service costs while improving satisfaction',
      'Large‑scale document processing with high accuracy',
      'Automation of routine compliance tasks with auditable oversight',
      'Operational savings through end‑to‑end workflow optimization',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Use case analysis, data readiness, safety requirements',
      pilot: '6-8 weeks: Agent development, guardrails, evaluation framework',
      production:
        '10-14 weeks: Scaling, MLOps integration, performance optimization',
    },
  },
];
